import { useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { USER_QUERY_KEYS } from './keys';

import { MAX_PAGE_SIZE } from 'constants/page';
import { fetchTeamMembers } from 'services/user';
import type { EmployeeLike } from 'interface/userUtils';
import type { QueryObject } from 'interface/queryParam';

interface TeamMembersPage<TItem> {
  data: TItem[];
  meta: {
    page: number;
    total: number;
  };
}

export function useFetchUserTeam<TItem = EmployeeLike>(
  userId: string | number,
  filter: QueryObject,
  shouldFetch = true
) {
  const queryFn = async ({ pageParam }: { pageParam: unknown }): Promise<TeamMembersPage<TItem>> => {
    const currentPage = typeof pageParam === 'number' ? pageParam : 1;
    const response = await fetchTeamMembers(userId, {
      ...filter,
      page: currentPage,
      size: MAX_PAGE_SIZE,
    });
    const safeData = Array.isArray(response.data) ? (response.data as TItem[]) : [];
    const safeMeta =
      typeof response.meta === 'object' && response.meta ? (response.meta as { page?: number; total?: number }) : {};

    return {
      data: safeData,
      meta: {
        // If API omits page, keep progressing from the page we requested.
        page: safeMeta.page ?? currentPage,
        total: safeMeta.total ?? 0,
      },
    };
  };

  const query = useInfiniteQuery<TeamMembersPage<TItem>, Error>({
    queryKey: [USER_QUERY_KEYS.TEAMS, userId, filter],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage: TeamMembersPage<TItem>) => {
      const { page, total } = lastPage.meta;
      const totalPages = Math.ceil(total / MAX_PAGE_SIZE);
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: shouldFetch && Boolean(userId),
  });

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = query;

  const allTeamMembers = data?.pages.flatMap((page) => page.data);

  useEffect(() => {
    const fetchAllPages = async (): Promise<void> => {
      if (hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
      }
    };

    fetchAllPages();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  return { ...query, data: allTeamMembers };
}
